import React from 'react'
import { useUserCount } from "app/hooks/useUserCount";
import { TypeExam, TypeExamAPI } from "app/constants/types/exam.types";
import { TypeEventsCalendarSchedule } from "app/components/Calendar/utils/Types";
import useEnrollmentTutorialModal from 'app/components/EnrollmentTutorialModal/useEnrollmentTutorialModal';
import { capitalizeFirstLetter } from 'app/utils';
import { useSelector, useDispatch } from 'react-redux';
import { selectExamStore } from 'store/ExamStore/slice/selector';
import { fetchEnrolledExams, fetchExams } from 'store/ExamStore/slice';
import { getUserInfo } from 'aws/lambda';

type Props = {}
type TypeCalendar = TypeEventsCalendarSchedule & { examData: TypeExam }

// Define user info interface based on the Lambda function response
interface UserInfo {
  cognitoUsername: string;
  purchasedHLQuantity: number;
  purchasedSLQuantity: number;
  enrolledHLQuantity: number;
  enrolledSLQuantity: number;
  updatedAt: number;
}

const useDashboardPage = (props: Props) => {
  const { showTutorialModal, handleTutorialModalClose } = useEnrollmentTutorialModal({});
  const dispatch = useDispatch();
  const exams = useSelector(selectExamStore);
  const [event, setEvent] = React.useState<any>(null);
  const { userCount, showUserCount } = useUserCount();
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  
  // Fetch exams and enrolled exams when the dashboard loads
  React.useEffect(() => {
    let isMounted = true; // Track mounted state
    setLoading(true); // Set loading state at the beginning
    
    // Let the Redux slice handle the sequencing
    const fetchData = async () => {
      try {
        // Our enhanced fetchExams action now handles the sequencing internally
        // It will check for enrolled exams first and fetch them if needed
        console.log('Starting exam data fetch sequence from Dashboard');
        dispatch(fetchExams());
        
        if (!isMounted) return; // Check if component is still mounted
        
        // Step 3: Fetch user info for quota information
        const response = await getUserInfo();
        if (isMounted) {
          setUserInfo(response);
          console.log('User info fetched successfully', response);
        }
      } catch (error) {
        console.error('Error in fetch sequence:', error);
        // Don't set loading to false here - let the finally block handle it
      } finally {
        // Only update loading state if component is still mounted
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    // Start the fetch sequence
    fetchData();
    
    // Add a timeout to prevent indefinite loading state
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 10000); // 10 second timeout
    
    // Cleanup function to prevent memory leaks and updates on unmounted components
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [dispatch]); // Added dispatch to dependencies

  const open = Boolean(event);

  const getSubjectColor = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("chinese")) return "red";
    if (lowerTitle.includes("english")) return "purple";
    if (lowerTitle.includes("math")) return "blue";
    return "#9e9e9e"; // light gray for other subjects
  };


  const transformToCalendarEvents = (
    exams: TypeExam[]
  ): TypeCalendar[] | undefined => {

    try{

      return exams.map((exam) => ({
        title: exam.title,
        subtitle: exam.description ? capitalizeFirstLetter(exam.description) : '',
        start: new Date(exam.rawData.examTime),
        end: new Date(exam.rawData.examTime + exam.rawData.examDuration * 60 * 1000),
        color: exam.subject ? getSubjectColor(exam.subject) : '',
        examData: exam
      }));

    }
    catch(err){

      return exams

    }
  };



  // Calculate available quotas
  const hlQuota = userInfo ? Math.max(0, userInfo.purchasedHLQuantity - userInfo.enrolledHLQuantity) : 0;
  const slQuota = userInfo ? Math.max(0, userInfo.purchasedSLQuantity - userInfo.enrolledSLQuantity) : 0;

  return {
    event,
    setEvent,
    examData: transformToCalendarEvents(exams.exams),
    userCount,
    showUserCount,
    open,
    userInfo,
    loading,
    hlQuota,
    slQuota,
    showTutorialModal,
    handleTutorialModalClose,
  }
}

export default useDashboardPage
